/**
 * Кто находится на этаже здания — ЕДИНЫЙ ответ для всех экранов.
 *
 * До 28.07 каждый экран считал по-своему, и на одном и том же ТЦ выходило три
 * разных числа: карта показывала 118 объектов на первом этаже, «Объём 3D» — 157,
 * а этажерка — 2 (она смотрела только POI и только `attrs.floor`, которого в
 * импортированных данных нет). Теперь правило одно, здесь:
 *
 *  - если у площадки заданы уровни (levels), этаж N — это N-й уровень по
 *    ordinal, и на этаже всё, у чего `level` совпадает;
 *  - если уровней нет (одно здание с этажностью), этаж берётся из
 *    `attrs.floor`, а без него объект считается первым этажом;
 *  - объект принадлежит зданию, если его точка (для юнита — центр контура)
 *    лежит внутри контура здания;
 *  - арендаторы и инфраструктура разделены по ролям таксономии: лифт, лестница,
 *    туалет и вход — не арендаторы, и в счётчике «сколько тут магазинов» им не
 *    место.
 */
import type { LonLat } from '@app/geo'
import { specOf } from './taxonomy'
import type { Poi, Unit, Venue } from './types'

/** Объект на этаже: юнит (со своим контуром) или точка. */
export interface FloorItem {
  readonly id: string
  readonly name: Readonly<Record<string, string>>
  readonly category: string
  /** Юнит нарисован контуром, POI — точкой. */
  readonly kind: 'unit' | 'poi'
  /** Точка на карте: для юнита — центр контура. */
  readonly point: LonLat
  readonly attrs?: Readonly<Record<string, unknown>>
}

export interface FloorOccupants {
  /** Уровень RVF этого этажа; null — у площадки нет уровней. */
  readonly levelId: string | null
  /** Арендаторы: магазины, кафе, услуги — всё, ради чего сюда идут. */
  readonly tenants: readonly FloorItem[]
  /** Инфраструктура: входы, туалеты, лифты, эскалаторы, парковка. */
  readonly amenities: readonly FloorItem[]
}

/** Уровни по возрастанию ordinal — порядок этажей снизу вверх. */
export function sortedLevels(venue: Venue): Venue['levels'] {
  return [...venue.levels].sort((a, b) => a.ordinal - b.ordinal)
}

/** Уровень для этажа 1..N (1 — самый нижний). null — уровней нет. */
export function levelIdOfFloor(venue: Venue, floor: number): string | null {
  const levels = sortedLevels(venue)
  if (levels.length < 2) return null
  return levels[floor - 1]?.id ?? null
}

/** Номер этажа (1-based) для уровня; 1 — если уровней нет. */
export function floorOfLevel(venue: Venue, levelId: string): number {
  const index = sortedLevels(venue).findIndex((l) => l.id === levelId)
  return index >= 0 ? index + 1 : 1
}

/**
 * Этажность здания: attrs.floors, а если не задана — число уровней площадки,
 * но ТОЛЬКО для здания-контейнера. Обычный магазин внутри ТЦ одноэтажный:
 * иначе у «RIMI» появлялась кнопка «Объём · 3 эт.» с этажами всего центра.
 */
export function buildingFloors(venue: Venue, unit: Unit): number {
  const own = Number(unit.attrs?.['floors'])
  if (Number.isFinite(own) && own > 0) return Math.min(Math.round(own), 60)
  return containerUnitIds(venue).has(unit.id) ? Math.max(1, sortedLevels(venue).length) : 1
}

/** Точка внутри кольца (ray casting). */
function pointInRing(point: readonly number[], ring: readonly (readonly number[])[]): boolean {
  const x = point[0] as number
  const y = point[1] as number
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]?.[0] as number
    const yi = ring[i]?.[1] as number
    const xj = ring[j]?.[0] as number
    const yj = ring[j]?.[1] as number
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

/**
 * Центр контура юнита. Замыкающую вершину (она дублирует первую) не учитываем:
 * иначе средняя точка вытянутого контура уезжает, и объект у стены здания
 * «выпадает» наружу — так один магазин ТЦ терялся в счётчике этажа.
 */
function unitCenter(unit: Unit): LonLat | null {
  const ring = unit.geometry.coordinates[0]
  if (!ring || ring.length === 0) return null
  const first = ring[0] as readonly number[]
  const last = ring[ring.length - 1] as readonly number[]
  const closed = ring.length > 1 && first[0] === last[0] && first[1] === last[1]
  const pts = closed ? ring.slice(0, -1) : ring
  let x = 0
  let y = 0
  for (const p of pts) {
    x += p[0] as number
    y += p[1] as number
  }
  return [x / pts.length, y / pts.length]
}

/** Этаж объекта: по уровню (если уровни есть) или по attrs.floor. */
function floorOf(
  venue: Venue,
  item: { level: string; attrs?: Readonly<Record<string, unknown>> },
): number {
  if (sortedLevels(venue).length >= 2) return floorOfLevel(venue, item.level)
  const own = Number(item.attrs?.['floor'])
  return Number.isFinite(own) && own > 0 ? Math.round(own) : 1
}

/** Инфраструктура — не арендатор: у неё служебная роль в таксономии. */
function isAmenity(venue: Venue, category: string): boolean {
  const role = specOf(venue, category).role
  return role === 'infra' || role === 'connector' || role === 'parking' || role === 'service'
}

function itemOfUnit(unit: Unit, point: LonLat): FloorItem {
  return {
    id: unit.id,
    name: unit.name,
    category: unit.category,
    kind: 'unit',
    point,
    ...(unit.attrs ? { attrs: unit.attrs } : {}),
  }
}

function itemOfPoi(poi: Poi): FloorItem {
  return {
    id: poi.id,
    name: poi.name,
    category: poi.category,
    kind: 'poi',
    point: poi.point,
    ...(poi.attrs ? { attrs: poi.attrs } : {}),
  }
}

/**
 * Все объекты здания на этаже floor (1-based). Здание-контейнер в список не
 * попадает — иначе оно считалось бы собственным арендатором.
 */
export function floorOccupants(venue: Venue, unitId: string, floor: number): FloorOccupants {
  const building = venue.units.find((u) => u.id === unitId)
  const ring = building?.geometry.coordinates[0]
  const levelId = levelIdOfFloor(venue, floor)
  if (!building || !ring) return { levelId, tenants: [], amenities: [] }
  // Арендаторы бывают только у здания-контейнера. У обычного магазина их нет:
  // раньше «внутрь» RIMI попадали магазины ЭТАЖОМ ВЫШЕ — они просто накрывают
  // его по координатам, — и карточка честно писала «Арендаторов: 8».
  if (!containerUnitIds(venue).has(building.id)) {
    return { levelId, tenants: [], amenities: [] }
  }

  const items = itemsInside(venue, building, ring, floor)
  return {
    levelId,
    tenants: items.filter((i) => !isAmenity(venue, i.category)),
    amenities: items.filter((i) => isAmenity(venue, i.category)),
  }
}

/** Объекты внутри контура здания на нужном этаже — юниты и точки. */
function itemsInside(
  venue: Venue,
  building: Unit,
  ring: readonly (readonly number[])[],
  floor: number,
): FloorItem[] {
  const out: FloorItem[] = []
  for (const unit of venue.units) {
    if (unit.id === building.id) continue
    const center = unitCenter(unit)
    if (!center || !pointInRing(center, ring)) continue
    if (floorOf(venue, unit) === floor) out.push(itemOfUnit(unit, center))
  }
  for (const poi of venue.pois) {
    if (!pointInRing(poi.point, ring)) continue
    if (floorOf(venue, poi) === floor) out.push(itemOfPoi(poi))
  }
  return out
}

/**
 * Сколько чужих центров должно попасть внутрь, чтобы считать юнит контейнером.
 * Три — мало: у импортированных планов крупный магазин (гипермаркет, кинотеатр)
 * задевает несколько соседей и сам становился «зданием». Десяток соседей
 * внутри — это уже точно оболочка ТЦ, а не магазин.
 */
const CONTAINER_MIN_INSIDE = 10

/** Сколько чужих центров лежит внутри контура (считаем до порога). */
function countInside(
  ring: readonly (readonly number[])[],
  centers: readonly { id: string; center: LonLat | null }[],
  selfId: string,
): number {
  let inside = 0
  for (const other of centers) {
    if (other.id === selfId || !other.center) continue
    if (pointInRing(other.center, ring)) inside++
    if (inside >= CONTAINER_MIN_INSIDE) return inside
  }
  return inside
}

/**
 * Здания-контейнеры: то, ВНУТРИ чего живут арендаторы — торговый центр, вокзал,
 * судно. Признак либо явный (задана этажность больше одного этажа), либо по
 * содержимому: внутри контура нарисован десяток чужих объектов.
 *
 * Порог именно десяток: у импортированных планов соседние магазины заходят друг
 * за друга на пару метров, и «внутри есть чужой центр» ловил обычные магазины —
 * тогда у RIMI появлялись собственные «арендаторы» (это были магазины этажом
 * выше, накрывающие его по координатам).
 */
export function containerUnitIds(venue: Venue): Set<string> {
  const out = new Set<string>()
  const centers = [
    ...venue.units.map((u) => ({ id: u.id, center: unitCenter(u) })),
    ...venue.pois.map((p) => ({ id: p.id, center: p.point as LonLat })),
  ]
  for (const unit of venue.units) {
    const ring = unit.geometry.coordinates[0]
    if (!ring) continue
    const floors = Number(unit.attrs?.['floors'])
    if (Number.isFinite(floors) && floors > 1) {
      out.add(unit.id)
      continue
    }
    if (countInside(ring, centers, unit.id) >= CONTAINER_MIN_INSIDE) out.add(unit.id)
  }
  return out
}

/**
 * Сколько арендаторов на уровне площадки — для панели этажей на карте.
 * Считается так же, как для здания: без контейнеров и без инфраструктуры.
 */
export function levelTenantCount(venue: Venue, levelId: string): number {
  const containers = containerUnitIds(venue)
  let count = 0
  for (const unit of venue.units) {
    if (unit.level !== levelId || containers.has(unit.id)) continue
    if (!isAmenity(venue, unit.category)) count++
  }
  for (const poi of venue.pois) {
    if (poi.level !== levelId) continue
    if (!isAmenity(venue, poi.category)) count++
  }
  return count
}

/** Сколько арендаторов на каждом этаже: [этаж 1, этаж 2, …]. */
export function tenantsPerFloor(venue: Venue, unitId: string): number[] {
  const building = venue.units.find((u) => u.id === unitId)
  if (!building) return []
  const count = buildingFloors(venue, building)
  const out: number[] = []
  for (let floor = 1; floor <= count; floor++) {
    out.push(floorOccupants(venue, unitId, floor).tenants.length)
  }
  return out
}
